/**
 * @friskai/frisk-js Framework Adapter Interface
 *
 * Defines the interface for framework-specific adapters that allow Frisk
 * to integrate with different AI/LLM frameworks.
 */

import type { RegisterToolProperties } from '../core/tool-registry'
import type {
  RedactedObject,
  RedactedSerializedObject,
  RedactOption,
} from '../utils/redact'

/**
 * Information about a tool call extracted by the framework adapter
 */
export interface ToolCallInfo {
  /**
   * Unique identifier for the tool call
   */
  id: string

  /**
   * Name of the tool being called
   */
  name: string

  /**
   * Arguments passed to the tool
   */
  args: Record<string, unknown>
}

/** Optional behavior flags used when wrapping framework tools. */
export type WrapToolOptions = {
  captureReasoning?: boolean
}

/** Default tool-wrapping behavior used by framework adapters. */
export const DefaultWrapToolOptions = {
  captureReasoning: true,
}

/**
 * Framework adapter interface
 *
 * Implement this interface to integrate Frisk with a specific AI/LLM framework.
 * The adapter handles framework-specific serialization, tool wrapping, and
 * extraction of tool call information.
 *
 * @typeParam ToolCallT - The type of tool call objects in the framework
 * @typeParam ToolT - The type of tool objects in the framework
 */
export interface FrameworkAdapter<ToolCallT = unknown, ToolT = unknown> {
  readonly agentFramework?: string | null
  readonly agentFrameworkVersion?: string | null

  /**
   * Serialize tool arguments to a JSON string with optional redaction
   *
   * @param toolArgs - The tool arguments to serialize
   * @param options - Optional redaction configuration
   * @returns Serialized and optionally redacted tool arguments
   */
  serializeToolArgs(
    toolArgs: Record<string, unknown> | null | undefined,
    options?: { redact?: RedactOption },
  ): RedactedSerializedObject

  /**
   * Serialize agent state to a JSON string with optional redaction
   *
   * @param agentState - The agent state to serialize
   * @param options - Optional redaction configuration
   * @returns Serialized and optionally redacted agent state
   */
  serializeAgentState(
    agentState: Record<string, unknown> | null | undefined,
    options?: { redact?: RedactOption },
  ): RedactedSerializedObject

  /**
   * Convert tool arguments to a dictionary with optional redaction
   *
   * @param toolArgs - The tool arguments to convert
   * @param options - Optional redaction configuration
   * @returns Redacted dictionary result
   */
  toolArgsToDict(
    toolArgs: Record<string, unknown>,
    options?: { redact?: RedactOption },
  ): RedactedObject

  /**
   * Convert agent state to a dictionary with optional redaction
   *
   * @param agentState - The agent state to convert
   * @param options - Optional redaction configuration
   * @returns Redacted dictionary result
   */
  agentStateToDict(
    agentState: Record<string, unknown>,
    options?: { redact?: RedactOption },
  ): RedactedObject

  /**
   * Extract tool call information from a framework-specific tool call object
   *
   * @param toolCall - The framework-specific tool call object
   * @returns Normalized tool call information
   */
  normalizeToolCall(toolCall: ToolCallT): ToolCallInfo

  /**
   * Extract the user query from agent state
   *
   * @param agentState - The agent state
   * @returns The user query string, or null if not found
   */
  extractPrompt(agentState: Record<string, unknown>): string | null

  /**
   * Wrap tools with enhanced functionality, such as capturing LLM reasoning
   *
   * @param tools - The tools to wrap
   * @returns Wrapped tools with enhanced functionality
   */
  wrapTools?(tools: Iterable<ToolT>, options?: WrapToolOptions): ToolT[]

  /**
   * Wrap a single tool with enhanced functionality, such as capturing LLM reasoning
   *
   * @param tools - The tool to wrap
   * @returns Wrapped tool with enhanced functionality
   */
  wrapTool?(tool: ToolT, options?: WrapToolOptions): ToolT

  /**
   * Extract tool-registration metadata for registering wrapped tools with Frisk.
   *
   * Returns null when the adapter cannot derive a registration payload for the tool.
   */
  extractRegisterToolProperties?(tool: ToolT): RegisterToolProperties | null
}
